import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import CartSkeleton from './skeletons/CartSkeleton';
import axios from 'axios';

const CartPage = () => {
  const { cart, removeFromCart, updateItemQuantity } = useCart();
  const [promoCode, setPromoCode] = useState(() => {
  return localStorage.getItem('promoCode') || '';
});
const [discount, setDiscount] = useState(() => {
  return parseFloat(localStorage.getItem('promoDiscount') || '0');
});

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 500); // short fake delay
    return () => clearTimeout(timeout);
  }, []);
  
  const handleApplyPromo = async () => {
  const formattedCode = promoCode.trim().toUpperCase();
  try {
    const res = await axios.post('/api/promos/validate', { code: formattedCode });
    if (res.data.valid) {
      setDiscount(5);
      localStorage.setItem('promoDiscount', '5');
      localStorage.setItem('promoCode', formattedCode);
      toast.success("Promo code applied!");
    }
  } catch (err) {
    setDiscount(0);
    localStorage.removeItem('promoDiscount');
    localStorage.removeItem('promoCode');
    toast.error(err.response?.data?.message || "Invalid promo code");
  }
};


  const subtotal = cart.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
  const discountAmount = discount;
  const total = (subtotal - discountAmount).toFixed(2);

  if (loading) return <CartSkeleton />;

  return (
    <div className="max-w-6xl mx-auto pt-20 p-4">
      <h2 className="text-3xl font-bold mb-6">Your Cart</h2>
  
      {cart.length === 0 ? (
        <p className="text-lg">Your cart is empty.</p>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Cart Items */}
          <div className="flex-1 bg-white rounded shadow p-4">
            <ul className="divide-y">
              <AnimatePresence>
                {cart.map((item, index) => (
                  <motion.li
                    key={`${item._id}-${item.size}-${index}`}
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.2 }}
                    className="py-4 flex items-center gap-4"
                  >
                    <img
                      src={item.photos?.[0] ? `/uploads/${item.photos[0].replace(/\\/g, '/')}` : '/fallback.jpg'}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded border"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg mb-1">{item.name}</h4>
                      <p className="text-sm text-gray-600">Weight: {item.weight}g</p>
                      <p className="text-sm text-gray-600">Size: <span className="font-medium">{item.size}</span></p>
                      <p className="text-blue-700 font-semibold mb-2">${item.price.toFixed(2)}</p>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateItemQuantity(item._id, item.quantity - 1, item.size)}
                          disabled={item.quantity <= 1}
                          className="px-2 py-1 bg-gray-200 rounded"
                        >
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          onClick={() => {
                            const matchedStock = item.sizes?.find(s => s.size === item.size)?.stock;

if (matchedStock !== undefined && item.quantity >= matchedStock) {
  toast.error("Stock limit reached");
} else {
  updateItemQuantity(item._id, item.quantity + 1, item.size);
}

                          }}
                          className="px-2 py-1 bg-gray-200 rounded"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <motion.button
                      onClick={() => removeFromCart(item._id, item.size)}
                      whileTap={{ scale: 0.9 }}
                      className="text-sm text-red-500 hover:underline"
                    >
                      Remove
                    </motion.button>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          </div>

          {/* Order Summary */}
          <div className="w-full lg:w-1/3 sticky top-4 bg-white rounded shadow p-4 h-fit">
            <h3 className="text-xl font-semibold mb-4">Order Summary</h3>
            <div className="flex justify-between mb-2">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Discount</span>
              <span className="text-green-600">-${discountAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Shipping</span>
              <span className="text-green-600 font-medium">Free</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total</span>
              <span>${total}</span>
            </div>

            <div className="mt-4">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Promo Code"
                className="border p-2 rounded w-full mb-2"
              />
              <button
                onClick={handleApplyPromo}
                className="w-full bg-gray-200 hover:bg-gray-300 text-sm py-2 rounded"
              >
                Apply Promo Code
              </button>
            </div>

            <button
              onClick={() => navigate('/checkout')}
              className="mt-6 w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 text-lg"
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
  
